import { module as BaseModule } from "modujs"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { SplitText } from "gsap/SplitText"
import { CustomEase } from "gsap/CustomEase"

gsap.registerPlugin(SplitText, ScrollTrigger, CustomEase)

export default class extends BaseModule {
  init() {
    const { effect } = this.el.dataset
    this[effect]()
  }

  headline() {
    if (this.el.textContent === "") return
    const element = this.el
    element.style.overflowX = "clip"

    let split
    let tweens = []
    let triggers = []
    let windowWidth = window.innerWidth

    const tl = () => {
      split = new SplitText(element, { type: "lines" })
      gsap.set(element, { autoAlpha: 1 })

      split.lines.forEach((line, i) => {
        tweens[i] = gsap.timeline({ paused: true }).from(line, {
          duration: 1,
          y: 25,
          opacity: 0,
          delay: i * 0.1,
          ease: "power4.out",
        })

        triggers[i] = ScrollTrigger.create({
          trigger: line,
          start: "top bottom",
          delay: 0.5,
          end: "bottom top",
          animation: tweens[i],
          once: true,
        })
      })
    }

    tl()

    const cleanup = () => {
      triggers.forEach((trigger) => trigger.kill())
      tweens.forEach((tween) => tween.kill())
      split.revert()

      tweens = []
      triggers = []
    }

    const handleResize = () => {
      if (windowWidth === window.innerWidth) return
      windowWidth = window.innerWidth

      cleanup()

      requestAnimationFrame(() => {
        tl()
      })
    }

    const debouncedResize = (() => {
      let timeout
      return () => {
        clearTimeout(timeout)
        timeout = setTimeout(handleResize, 200)
      }
    })()

    window.addEventListener("resize", debouncedResize)
  }

  nested() {
    const elements = Array.from(this.el.children)

    elements.forEach((element) => {
      this.text(element)
    })
  }

  text(element) {
    const text = element || this.el

    if (text.textContent === "") return
    text.style.opacity = 1

    let trigger, split, tween
    let windowWidth = window.innerWidth

    const tl = () => {
      split = new SplitText(text, { type: "lines" })
      const { lines } = split

      tween = gsap.timeline()
      tween.from(lines, {
        opacity: 0,
        stagger: 0.15,
        duration: 1.5,
      })
      trigger = ScrollTrigger.create({
        animation: tween,
        trigger: text,
        start: "top bottom",
        delay: 0.5,
        end: "bottom bottom",
      })
    }

    tl()

    const handleResize = () => {
      if (windowWidth === window.innerWidth) return
      windowWidth = window.innerWidth // Update reference width

      trigger.kill()
      tween.kill()
      split.revert()

      requestAnimationFrame(() => {
        tl()
      })
    }

    const debouncedResize = (() => {
      let timeout
      return () => {
        clearTimeout(timeout)
        timeout = setTimeout(handleResize, 200)
      }
    })()

    window.addEventListener("resize", debouncedResize)
  }

  blur() {
    const animation = gsap
      .timeline({ paused: true })
      .set(this.el, { filter: "blur(20px)", scale: 1.1, opacity: 0 })
      .to(this.el, {
        filter: "blur(0px)",
        opacity: 1,
        scale: 1.05,
        ease: "power3.out",
        duration: 2,
      })

    ScrollTrigger.create({
      trigger: this.el,
      start: "top bottom",
      end: "bottom top",
      animation,
      once: true,
    })
  }

  header() {
    const logo = document.querySelector("[data-effect-logo]")
    const menu = document.querySelector(".menu-desktop")
    const menuItems = document.querySelectorAll(".menu-desktop a")
    const cta = document.querySelector(".menu-cta")

    menu.style.opacity = 1
    const animation = gsap
      .timeline()
      .to(logo, { opacity: 1 })
      .fromTo(
        menuItems,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: 1,
          stagger: {
            each: 0.1,
          },
        },
        0
      )
      .fromTo(
        cta,
        {
          opacity: 0,
          y: 5,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.inOut",
        },
        0.25
      )
  }

  footer() {
    const logo = this.el.querySelector(".footer-logo")
    const address = this.el.querySelectorAll(".footer-address")
    const menu = this.el.querySelector(".footer-menu")
    const privacy = this.el.querySelector(".footer-privacy")
    const separator = this.el.querySelector(".footer-separator")
    const credit = this.el.querySelector(".footer-credit")
    const copyright = this.el.querySelector(".footer-copyright")

    const animation = gsap
      .timeline({ paused: true })
      .to(logo, { opacity: 1 })
      .to(address, { opacity: 1 }, "-=0.25")
      .to(menu, { opacity: 1 })
      .to(separator, { opacity: 1, scaleX: 1, transformOrigin: "left" })
      .to([privacy, copyright], { opacity: 1 })
      .to(credit, { opacity: 1 })

    ScrollTrigger.create({
      animation,
      trigger: this.el,
      start: "50% 100%",
      end: "max",
      scrub: 2,
    })
  }
}
